import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PersonalDetailsStep = ({ data, onComplete, onCancel }) => {
  const [formData, setFormData] = useState({
    fullName: data?.fullName || '',
    dateOfBirth: data?.dateOfBirth || '',
    tinNumber: data?.tinNumber || '',
    email: data?.email || '',
    phone: data?.phone || '',
    residentialAddress: data?.residentialAddress || '',
    province: data?.province || '',
    district: data?.district || '',
    citizenshipStatus: data?.citizenshipStatus || 'png_citizen'
  });
  const [errors, setErrors] = useState({});

  const provinceOptions = [
    { value: 'central', label: 'Central Province' },
    { value: 'chimbu', label: 'Chimbu Province' },
    { value: 'eastern_highlands', label: 'Eastern Highlands Province' },
    { value: 'east_new_britain', label: 'East New Britain Province' },
    { value: 'east_sepik', label: 'East Sepik Province' },
    { value: 'enga', label: 'Enga Province' },
    { value: 'gulf', label: 'Gulf Province' },
    { value: 'hela', label: 'Hela Province' },
    { value: 'jiwaka', label: 'Jiwaka Province' },
    { value: 'madang', label: 'Madang Province' },
    { value: 'manus', label: 'Manus Province' },
    { value: 'milne_bay', label: 'Milne Bay Province' },
    { value: 'morobe', label: 'Morobe Province' },
    { value: 'ncr', label: 'National Capital District (Port Moresby)' },
    { value: 'new_ireland', label: 'New Ireland Province' },
    { value: 'oro', label: 'Oro Province (Northern)' },
    { value: 'southern_highlands', label: 'Southern Highlands Province' },
    { value: 'west_new_britain', label: 'West New Britain Province' },
    { value: 'western', label: 'Western Province' },
    { value: 'western_highlands', label: 'Western Highlands Province' },
    { value: 'west_sepik', label: 'West Sepik Province (Sandaun)' }
  ];

  const citizenshipOptions = [
    { value: 'png_citizen', label: 'PNG Citizen' },
    { value: 'permanent_resident', label: 'Permanent Resident' },
    { value: 'work_permit', label: 'Work Permit Holder' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateTIN = (tin) => {
    // PNG TIN format: 9 digits
    const tinRegex = /^\d{9}$/;
    return tinRegex?.test(tin);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePhone = (phone) => {
    // PNG phone format: starts with +675 or 675 or local format
    const phoneRegex = /^(\+?675)?[0-9]{7,8}$/;
    return phoneRegex?.test(phone?.replace(/\s/g, ''));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const age = new Date()?.getFullYear() - new Date(formData?.dateOfBirth)?.getFullYear();
      if (age < 16) {
        newErrors.dateOfBirth = 'Employee must be at least 16 years old';
      }
      if (age > 100) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    if (!formData?.tinNumber?.trim()) {
      newErrors.tinNumber = 'TIN number is required';
    } else if (!validateTIN(formData?.tinNumber)) {
      newErrors.tinNumber = 'TIN must be 9 digits';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData?.phone)) {
      newErrors.phone = 'Please enter a valid PNG phone number';
    }

    if (!formData?.residentialAddress?.trim()) {
      newErrors.residentialAddress = 'Residential address is required';
    }

    if (!formData?.province) {
      newErrors.province = 'Province is required';
    }

    if (!formData?.citizenshipStatus) {
      newErrors.citizenshipStatus = 'Citizenship status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onComplete?.(formData);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Personal Details</h2>
        <p className="text-gray-600">
          Enter employee personal information for NASFUND registration
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Full Name */}
        <Input
          label="Full Name (as per ID)"
          type="text"
          required
          value={formData?.fullName}
          onChange={(e) => handleChange('fullName', e?.target?.value)}
          error={errors?.fullName}
          placeholder="John Doe"
        />

        {/* Date of Birth */}
        <Input
          label="Date of Birth"
          type="date"
          required
          value={formData?.dateOfBirth}
          onChange={(e) => handleChange('dateOfBirth', e?.target?.value)}
          error={errors?.dateOfBirth}
        />

        {/* TIN Number */}
        <div>
          <Input
            label="Tax Identification Number (TIN)"
            type="text"
            required
            value={formData?.tinNumber}
            onChange={(e) => handleChange('tinNumber', e?.target?.value)}
            error={errors?.tinNumber}
            placeholder="123456789"
            maxLength={9}
          />
          <p className="text-xs text-gray-500 mt-1">
            9-digit TIN issued by PNG Internal Revenue Commission
          </p>
        </div>

        {/* Email */}
        <Input
          label="Email Address"
          type="email"
          required
          value={formData?.email}
          onChange={(e) => handleChange('email', e?.target?.value)}
          error={errors?.email}
          placeholder="john.doe@example.com"
        />

        {/* Phone */}
        <div>
          <Input
            label="Phone Number"
            type="tel"
            required
            value={formData?.phone}
            onChange={(e) => handleChange('phone', e?.target?.value)}
            error={errors?.phone}
            placeholder="+675 7123 4567"
          />
          <p className="text-xs text-gray-500 mt-1">
            PNG format: +675 followed by 7-8 digits
          </p>
        </div>

        {/* Residential Address */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Residential Address <span className="text-destructive">*</span>
          </label>
          <textarea
            value={formData?.residentialAddress}
            onChange={(e) => handleChange('residentialAddress', e?.target?.value)}
            rows={3}
            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Street address, suburb, city"
          />
          {errors?.residentialAddress && (
            <p className="text-xs text-destructive mt-1">{errors?.residentialAddress}</p>
          )}
        </div>

        {/* Province and District */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Province"
            required
            options={provinceOptions}
            value={formData?.province}
            onChange={(value) => handleChange('province', value)}
            error={errors?.province}
            placeholder="Select province"
          />

          <Input
            label="District"
            type="text"
            value={formData?.district}
            onChange={(e) => handleChange('district', e?.target?.value)}
            placeholder="Enter district"
          />
        </div>

        {/* Citizenship Status */}
        <Select
          label="Citizenship Status"
          required
          options={citizenshipOptions}
          value={formData?.citizenshipStatus}
          onChange={(value) => handleChange('citizenshipStatus', value)}
          error={errors?.citizenshipStatus}
        />

        {/* NASFUND Eligibility Check */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="CheckCircle" size={20} className="text-emerald-600 mr-3 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900 mb-1">NASFUND Eligibility</p>
              <p className="text-sm text-emerald-700">
                All employees working in PNG are eligible for NASFUND membership. 
                Contributions will be calculated based on employment details in the next step.
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
          >
            Continue to Employment Information
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PersonalDetailsStep;